import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { MainLayout } from '@/components/layout/MainLayout'
import { TaskList } from '@/components/tasks/TaskList'
import { TaskForm } from '@/components/tasks/TaskForm'
import { RunControls } from '@/components/pipeline/RunControls'
import { RunSummary } from '@/components/pipeline/RunSummary'
import { LogViewer } from '@/components/pipeline/LogViewer'
import { ArtifactViewer } from '@/components/artifacts/ArtifactViewer'
import { LoginForm } from '@/components/auth/LoginForm'
import { UserManagement } from '@/components/admin/UserManagement'
import { Settings } from '@/components/admin/Settings'
import { ClientsManagement } from '@/components/admin/ClientsManagement'
import { TaskHistory } from '@/components/tasks/TaskHistory'
import { useAuthStore } from '@/store/useAuthStore'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

type View = 'dashboard' | 'users' | 'clients' | 'settings'

function Dashboard({ currentView, setCurrentView }: { currentView: View; setCurrentView: (view: View) => void }) {
  const { user } = useAuthStore()

  return (
    <MainLayout sidebar={<TaskList />} currentView={currentView} setCurrentView={setCurrentView}>
      {currentView === 'users' && user?.role === 'admin' ? (
        <UserManagement />
      ) : currentView === 'clients' ? (
        <ClientsManagement />
      ) : currentView === 'settings' && user?.role === 'admin' ? (
        <Settings />
      ) : (
        <div className="space-y-4">
          <TaskForm />
          <RunControls />
          <RunSummary />
          <TaskHistory />
          <LogViewer />
          <ArtifactViewer />
        </div>
      )}
    </MainLayout>
  )
}

function App() {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const [currentView, setCurrentView] = useState<View>('dashboard')

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    const handleUnauthorized = () => {
      checkAuth()
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [checkAuth])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        {!isAuthenticated ? <LoginForm /> : <Dashboard currentView={currentView} setCurrentView={setCurrentView} />}
        <Toaster position="top-right" />
      </ErrorBoundary>
    </QueryClientProvider>
  )
}

export default App
